/**
 * GWP Automation - Gift With Purchase Controller
 *
 * Monitors cart and automatically adds/removes free gift based on threshold.
 * Configuration loaded from metaobjects via gwp-config.liquid snippet.
 *
 * Key features:
 * - Mutex guard prevents concurrent operations
 * - Cart token tracking prevents self-triggered loops
 * - Opt-out persistence for customer control
 * - Debounced updates for performance
 */

class GwpController extends HTMLElement {
  constructor() {
    super();

    this.config = null;
    this.activeCampaign = null;

    // State management
    this.isProcessing = false;
    this.lastCartToken = null;
    this.debounceTimer = null;

    // Bind methods
    this.onCartUpdate = this.onCartUpdate.bind(this);
  }

  connectedCallback() {
    // Load config from JSON
    this.config = this.loadConfig();

    if (!this.config.enabled) {
      console.log('[GWP] No active campaigns');
      return;
    }

    // Get highest priority campaign
    this.activeCampaign = this.getActiveCampaign();
    if (!this.activeCampaign) {
      console.log('[GWP] No valid campaign found');
      return;
    }

    // Store initial cart token
    this.lastCartToken = this.config.cartToken;

    // Subscribe to cart updates (pubsub only - avoid double handling)
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      this.unsubscribe = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate
      );
    }

    // Initial evaluation
    this.initialCheck();

    console.log('[GWP] Controller initialized', {
      campaign: this.activeCampaign.name,
      threshold: this.activeCampaign.thresholdCents / 100
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    clearTimeout(this.debounceTimer);
  }

  /**
   * Load configuration from gwp-config.liquid JSON
   */
  loadConfig() {
    const el = document.getElementById('gwp-config');
    if (!el) {
      console.warn('[GWP] Config element not found');
      return { enabled: false, campaigns: [] };
    }

    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[GWP] Config parse error:', e);
      return { enabled: false, campaigns: [] };
    }
  }

  /**
   * Get highest priority active campaign
   */
  getActiveCampaign() {
    if (!this.config.campaigns.length) return null;

    // Sort by priority (lower = higher priority)
    const sorted = [...this.config.campaigns].sort((a, b) => a.priority - b.priority);
    return sorted[0];
  }

  /**
   * Initial cart check on page load
   */
  async initialCheck() {
    try {
      const cart = await this.fetchCart();
      await this.evaluateCart(cart, true);
    } catch (e) {
      console.error('[GWP] Initial check failed:', e);
    }
  }

  /**
   * Handle cart update events from pubsub
   */
  onCartUpdate(event) {
    const cart = event?.cart;
    if (!cart) return;

    // Skip if this is our own update (same token means we triggered it)
    if (cart.token === this.lastCartToken && this.isProcessing) {
      return;
    }

    // Debounce rapid updates
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.evaluateCart(cart, false);
    }, 250);
  }

  /**
   * Main cart evaluation logic
   */
  async evaluateCart(cart, isInitial = false) {
    if (this.isProcessing) return;
    if (!this.activeCampaign) return;

    const campaign = this.activeCampaign;
    const analysis = this.analyzeCart(cart, campaign);

    // Update progress UI
    this.dispatchProgressUpdate(analysis, campaign);

    // Check opt-out status
    const isOptedOut = cart.attributes?.gwp_opt_out === 'true';

    // Clear opt-out if cart dropped below threshold
    if (isOptedOut && !analysis.isEligible) {
      await this.clearOptOut();
    }

    // Determine required action
    const shouldHaveGwp = analysis.isEligible && !isOptedOut;

    if (shouldHaveGwp && !analysis.hasGwp) {
      await this.addGwp(campaign);
    } else if (!shouldHaveGwp && analysis.hasGwp) {
      await this.removeGwp(analysis.gwpLineKey, false);
    }
  }

  /**
   * Analyze cart contents
   */
  analyzeCart(cart, campaign) {
    let eligibleTotal = 0;
    let hasGwp = false;
    let gwpLineKey = null;

    for (const item of cart.items || []) {
      // Check if this is the GWP item
      const isGwpItem =
        String(item.variant_id) === String(campaign.giftVariantId) ||
        (item.properties && item.properties._gwp === 'true');

      if (isGwpItem) {
        hasGwp = true;
        gwpLineKey = item.key;
      } else {
        // Sum non-GWP items (use final_line_price for post-discount value)
        eligibleTotal += item.final_line_price || item.line_price || 0;
      }
    }

    const isEligible = eligibleTotal >= campaign.thresholdCents;
    const amountNeeded = Math.max(0, campaign.thresholdCents - eligibleTotal);
    const progressPercent = Math.min(100, (eligibleTotal / campaign.thresholdCents) * 100);

    return {
      eligibleTotal,
      hasGwp,
      gwpLineKey,
      isEligible,
      amountNeeded,
      progressPercent
    };
  }

  /**
   * Add GWP to cart
   */
  async addGwp(campaign) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    console.log('[GWP] Adding gift...');

    try {
      const sections = this.getSections();

      const response = await fetch(this.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(campaign.giftVariantId),
            quantity: 1,
            properties: {
              _gwp: 'true',
              _gwp_campaign: campaign.handle
            }
          }],
          sections
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 422) {
          console.log('[GWP] Gift unavailable (out of stock)');
          this.dispatchEvent('gwp:unavailable', { campaign });
          return;
        }
        throw new Error(error.description || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Update cart token and refresh UI
      await this.refreshCartUI(sections, data.sections);

      console.log('[GWP] Gift added successfully');
      this.dispatchEvent('gwp:added', { campaign });

    } catch (e) {
      console.error('[GWP] Add failed:', e);
      this.dispatchEvent('gwp:error', { error: e.message, action: 'add' });
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Remove GWP from cart
   */
  async removeGwp(lineKey, isManualRemoval = false) {
    if (this.isProcessing) return;
    if (!lineKey) return;

    this.isProcessing = true;

    console.log('[GWP] Removing gift...', { isManualRemoval });

    try {
      const sections = this.getSections();

      const response = await fetch(this.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Set opt-out if manual removal
      if (isManualRemoval) {
        await this.setOptOut();
      }

      await this.refreshCartUI(sections, data.sections);

      console.log('[GWP] Gift removed successfully');
      this.dispatchEvent('gwp:removed', { isManualRemoval });

    } catch (e) {
      console.error('[GWP] Remove failed:', e);
      this.dispatchEvent('gwp:error', { error: e.message, action: 'remove' });
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Set opt-out cart attribute (prevents re-add after manual removal)
   */
  async setOptOut() {
    try {
      await fetch(this.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { gwp_opt_out: 'true' }
        })
      });
      console.log('[GWP] Opt-out set');
    } catch (e) {
      console.error('[GWP] Failed to set opt-out:', e);
    }
  }

  /**
   * Clear opt-out when cart drops below threshold
   */
  async clearOptOut() {
    try {
      await fetch(this.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { gwp_opt_out: '' }
        })
      });
      console.log('[GWP] Opt-out cleared');
    } catch (e) {
      console.error('[GWP] Failed to clear opt-out:', e);
    }
  }

  /**
   * Get sections to update (using theme's pattern)
   */
  getSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections }
      })
    );
    return sections;
  }

  /**
   * Refresh cart UI after modification
   */
  async refreshCartUI(sectionIds, sectionsData) {
    try {
      const cart = await this.fetchCart();

      // Update our token reference
      this.lastCartToken = cart.token;

      // Attach sections data
      if (sectionsData) {
        cart.sections = sectionsData;
      }

      // Publish update (this will trigger UI refresh)
      if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          { cart }
        );
      }
    } catch (e) {
      console.error('[GWP] Refresh failed:', e);
    }
  }

  /**
   * Fetch current cart state
   */
  async fetchCart() {
    const response = await fetch(`${this.routes.cart}.js`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  /**
   * Dispatch progress update for UI components
   */
  dispatchProgressUpdate(analysis, campaign) {
    document.dispatchEvent(new CustomEvent('gwp:progress', {
      detail: {
        eligibleTotal: analysis.eligibleTotal,
        threshold: campaign.thresholdCents,
        amountNeeded: analysis.amountNeeded,
        percent: analysis.progressPercent,
        isEligible: analysis.isEligible,
        hasGwp: analysis.hasGwp,
        campaign
      }
    }));
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  /**
   * Route helpers
   */
  get routes() {
    const base = typeof FoxTheme !== 'undefined' && FoxTheme.routes;
    return {
      cart: base?.cart_url || '/cart',
      cartAdd: base?.cart_add_url || '/cart/add.js',
      cartChange: base?.cart_change_url || '/cart/change.js',
      cartUpdate: base?.cart_update_url || '/cart/update.js'
    };
  }
}

// Register custom element
if (!customElements.get('gwp-controller')) {
  customElements.define('gwp-controller', GwpController);
}


/**
 * GWP Remove Button - Handles manual gift removal with opt-out
 */
class GwpRemoveButton extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  async handleClick(e) {
    e.preventDefault();

    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    // Find the controller and trigger manual removal
    const controller = document.querySelector('gwp-controller');
    if (controller && typeof controller.removeGwp === 'function') {
      await controller.removeGwp(lineKey, true); // true = manual removal
    }
  }
}

if (!customElements.get('gwp-remove-button')) {
  customElements.define('gwp-remove-button', GwpRemoveButton, { extends: 'button' });
}
